import { useDispatch, useSelector } from "react-redux";
import {
  selectCurrentCircle,
  selectIsLoading,
} from "../../redux/selectors/circleSelector";
import { Box, Container, Grid, Stack, Typography } from "@mui/material";
import {
  CircleCard,
  CircleSearch,
  LoadingSpinner,
} from "../allComponents/AllComponents";
import { useEffect, useState } from "react";
import DrawerNavbar from "../drawerNavBar/DrawerNavbar";
import {
  selectCurrentUser,
  selectIsError,
} from "../../redux/selectors/userSelector";
import UserProfileHeader from "../userprofile-header/UserProfileHeader";
import { RoleNames } from "../../utility/types/rolesType";
import { useNavigate } from "react-router-dom";
import { fetchCircleByLeader } from "../../redux/actions/circleAction";
import { AppDispatch } from "../../redux/store/store";

interface Circle {
  _id?: string;
  circleName?: string;
  circleImage?: string;
}

const LeaderCircle = () => {
  const navigate = useNavigate();

  const dispatch = useDispatch<AppDispatch>();

  const isLoading = useSelector(selectIsLoading);

  const currentUser = useSelector(selectCurrentUser);

  const circleData = useSelector(selectCurrentCircle);

  const error = useSelector(selectIsError);

  const [initialLoading, setInitialLoading] = useState(true);

  const [openNav, setOpenNav] = useState(false);

  // 1. Add state for search input and selected circle
  const [searchInput, setSearchInput] = useState<string>("");
  const [selectedCircle, setSelectedCircle] = useState<Circle | null>(null);

  useEffect(() => {
    const fetchUnions = async () => {
      // await dispatch(fetchAllCircles());
      await dispatch(fetchCircleByLeader(currentUser?._id ?? ""));
      setInitialLoading(false);
    };

    fetchUnions();

    return () => {
      setInitialLoading(true);
    };
  }, [dispatch, currentUser?._id]);

  const safeCircleData = Array?.isArray(circleData) ? circleData : [];

  const isLeader =
    circleData &&
    Array?.isArray(circleData) &&
    circleData?.some((circle: any) => {
      return circle.leader && circle.leader?._id === currentUser?._id;
    });

  const isAdmin = currentUser?.roles?.some((r: any) =>
    [RoleNames.ADMIN, RoleNames.SUPER_ADMIN].includes(r.roleName as RoleNames)
  );

  if (initialLoading || isLoading) {
    return (
      <LoadingSpinner
        loading={true}
        size={70}
        sx={{
          position: "absolute",
        }}
      />
    );
  }

  const handleClick = async (id: string) => {
    await navigate(`/circle/${id}`);
  };

  // 2. Add the filtering logic
  const filteredCircle = safeCircleData.filter((circle: Circle) =>
    circle.circleName?.toLowerCase().includes(searchInput.toLowerCase())
  );

  // 3. Add handlers for search input change, circle selection, and reset
  const handleSearchInputChange = (inputValue: string) => {
    setSearchInput(inputValue);
    setSelectedCircle(null); // Clear selected circle when search input changes
  };

  const handleSearchSelect = (circle: Circle | null) => {
    setSelectedCircle(circle);
    setSearchInput(""); // Clear search input when a circle is selected
  };

  const handleReset = () => {
    setSearchInput("");
    setSelectedCircle(null); // Reset to show all circles
  };

  return (
    <>
      {(isLeader || isAdmin) && (
        <>
          <UserProfileHeader onOpenNav={() => setOpenNav(true)} />

          <Box
            sx={{
              minHeight: 1,
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
            }}
          >
            <DrawerNavbar
              openNav={openNav}
              onCloseNav={() => setOpenNav(false)}
            />

            {isLoading ? (
              <LoadingSpinner
                loading={isLoading}
                size={70}
                sx={{
                  position: "absolute",
                }}
              />
            ) : (
              <Container>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={5}
                  mt={15}
                >
                  <Typography variant="h4">
                    {circleData.length > 1
                      ? `Your Current Circles As A Leader`
                      : `Your Current Circle As A Leader`}
                  </Typography>
                </Stack>

                <Stack
                  mb={5}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <CircleSearch
                    circles={circleData}
                    selectedCircle={selectedCircle}
                    onSearchInputChange={handleSearchInputChange}
                    onSearchSelect={handleSearchSelect}
                    onReset={handleReset}
                  />
                </Stack>

                <Grid container spacing={3}>
                  {(selectedCircle ? [selectedCircle] : filteredCircle).map(
                    (circle: any, index) => {
                      const isMember = currentUser?.unions?.some(
                        (u) => u._id === circle._id
                      );

                      return (
                        <CircleCard
                          key={circle._id}
                          _id={circle._id}
                          index={index}
                          circleName={circle.circleName}
                          circleImage={circle.circleImage}
                          description={circle.description}
                          leader={circle.leader}
                          configuration={circle.configuration}
                          isMember={isMember}
                          payOuts={circle.payOuts}
                          updatedAt={circle.updated_At}
                          totalMembers={circle.members.length}
                          onClick={() => handleClick(circle._id)}
                        />
                      );
                    }
                  )}
                </Grid>
              </Container>
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default LeaderCircle;
